import {
  ironAndSteelAgents,
  IronAndSteelEmissionQualifyingParams,
  EmissionQualifyingParams,
  qpd20Schema
} from '@/shared/api/EmissionQualifyingParamsModel'
import { PRODUCTION_METHOD_TO_QPD, UnitLabel } from '@/shared/config/config'
import { isValidPercentage, NumericInputGroup, SelectGroup } from '@/common'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQpdWithItems } from './hooks/useQpdWithItems'
import QpdWithItemsEditor from './QpdWithItemsEditor'
const MAX_OTHER_ELEMENTS = 4

export default function IronAndSteelQualifyingParamsForm({
  productionMethodCodes,
  qualifyingParams,
  setQualifyingParams,
  requiredQualifyingParamsCodes
}: {
  productionMethodCodes: string[]
  qualifyingParams: Partial<IronAndSteelEmissionQualifyingParams>
  setQualifyingParams: (params: Partial<EmissionQualifyingParams>) => void
  requiredQualifyingParamsCodes: string[]
}) {
  const { t } = useTranslation()

  const qualifyingParametersToFill = useMemo(
    () => productionMethodCodes.map(code => PRODUCTION_METHOD_TO_QPD[code]).flat(),
    [productionMethodCodes]
  )

  const onQpd20Change = useCallback(
    (value: number | null, items: { name: string; value: number }[]) => {
      setQualifyingParams({
        ...qualifyingParams,
        QPD20: { value, materials: items }
      })
    },
    [qualifyingParams, setQualifyingParams]
  )

  const { onNameChange, onValueChange, onRemoveOtherElement, onAddOtherElement } = useQpdWithItems({
    initialValue: {
      value: qualifyingParams.QPD20?.value || null,
      items: qualifyingParams.QPD20?.materials || []
    },
    maxItems: MAX_OTHER_ELEMENTS,
    setQualifyingParamValue: onQpd20Change
  })

  const isCodeRelevant = useCallback(
    (code: string) => {
      return qualifyingParametersToFill.find(param => param.code === code)
    },
    [qualifyingParametersToFill]
  )

  const onNumericInputChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = evt.target
    setQualifyingParams({
      ...qualifyingParams,
      [name]: {
        value: value ? parseFloat(value) : null
      }
    })
  }

  const onSelectChange = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = evt.target
    setQualifyingParams({
      ...qualifyingParams,
      [name]: { value }
    })
  }

  const ironAndSteelAgentsOptions = ironAndSteelAgents.map(({ value, key }) => ({
    label: t(`emissionQualifyingParams:ironAndSteelAgents.${key}`),
    value
  }))

  const qpd10 = (
    <SelectGroup
      required={requiredQualifyingParamsCodes.includes('QPD10')}
      label={t('emissionQualifyingParams:fields.QPD10.label')}
      name="QPD10"
      hint={t('emissionQualifyingParams:fields.QPD10.hint')}
      value={qualifyingParams.QPD10?.value || ''}
      options={ironAndSteelAgentsOptions}
      onChange={onSelectChange}
    />
  )

  const qpd11 = (
    <SelectGroup
      required={requiredQualifyingParamsCodes.includes('QPD11')}
      label={t('emissionQualifyingParams:fields.QPD11.label')}
      hint={t('emissionQualifyingParams:fields.QPD11.hint')}
      name="QPD11"
      value={qualifyingParams.QPD11?.value || ''}
      options={ironAndSteelAgentsOptions}
      onChange={onSelectChange}
    />
  )

  const qpd12 = (
    <NumericInputGroup
      required={requiredQualifyingParamsCodes.includes('QPD12')}
      label={t('emissionQualifyingParams:fields.QPD12.label')}
      hint={t('emissionQualifyingParams:fields.QPD12.hint')}
      value={qualifyingParams.QPD12?.value?.toString() || ''}
      name="QPD12"
      onChange={onNumericInputChange}
      suffix={UnitLabel.percent}
      numericFormat={{
        decimalScale: 3,
        isAllowed: isValidPercentage
      }}
    />
  )

  const qpd13 = (
    <NumericInputGroup
      required={requiredQualifyingParamsCodes.includes('QPD13')}
      label={t('emissionQualifyingParams:fields.QPD13.label')}
      hint={t('emissionQualifyingParams:fields.QPD13.hint')}
      value={qualifyingParams.QPD13?.value?.toString() || ''}
      name="QPD13"
      onChange={onNumericInputChange}
      suffix={UnitLabel.percent}
      numericFormat={{
        decimalScale: 3,
        isAllowed: isValidPercentage
      }}
    />
  )

  const qpd14 = (
    <NumericInputGroup
      required={requiredQualifyingParamsCodes.includes('QPD14')}
      label={t('emissionQualifyingParams:fields.QPD14.label')}
      hint={t('emissionQualifyingParams:fields.QPD14.hint')}
      value={qualifyingParams.QPD14?.value?.toString() || ''}
      name="QPD14"
      onChange={onNumericInputChange}
      suffix={UnitLabel.percent}
      numericFormat={{
        decimalScale: 3,
        isAllowed: isValidPercentage
      }}
    />
  )

  const qpd15 = (
    <NumericInputGroup
      required={requiredQualifyingParamsCodes.includes('QPD15')}
      label={t('emissionQualifyingParams:fields.QPD15.label')}
      hint={t('emissionQualifyingParams:fields.QPD15.hint')}
      value={qualifyingParams.QPD15?.value?.toString() || ''}
      name="QPD15"
      onChange={onNumericInputChange}
      suffix={UnitLabel.percent}
      numericFormat={{
        decimalScale: 3,
        isAllowed: isValidPercentage
      }}
    />
  )

  const qpd16 = (
    <NumericInputGroup
      required={requiredQualifyingParamsCodes.includes('QPD16')}
      label={t('emissionQualifyingParams:fields.QPD16.label')}
      hint={t('emissionQualifyingParams:fields.QPD16.hint')}
      value={qualifyingParams.QPD16?.value?.toString() || ''}
      name="QPD16"
      onChange={onNumericInputChange}
      suffix={UnitLabel.percent}
      numericFormat={{
        decimalScale: 3,
        isAllowed: isValidPercentage
      }}
    />
  )

  const qpd17 = (
    <NumericInputGroup
      required={requiredQualifyingParamsCodes.includes('QPD17')}
      label={t('emissionQualifyingParams:fields.QPD17.label')}
      hint={t('emissionQualifyingParams:fields.QPD17.hint')}
      value={qualifyingParams.QPD17?.value?.toString() || ''}
      name="QPD17"
      onChange={onNumericInputChange}
      suffix={UnitLabel.t}
    />
  )

  const qpd18 = (
    <NumericInputGroup
      required={requiredQualifyingParamsCodes.includes('QPD18')}
      label={t('emissionQualifyingParams:fields.QPD18.label')}
      hint={t('emissionQualifyingParams:fields.QPD18.hint')}
      value={qualifyingParams.QPD18?.value?.toString() || ''}
      name="QPD18"
      onChange={onNumericInputChange}
      suffix={UnitLabel.percent}
      numericFormat={{
        decimalScale: 3,
        isAllowed: isValidPercentage
      }}
    />
  )

  const qpd19 = (
    <NumericInputGroup
      required={requiredQualifyingParamsCodes.includes('QPD19')}
      label={t('emissionQualifyingParams:fields.QPD19.label')}
      hint={t('emissionQualifyingParams:fields.QPD19.hint')}
      value={qualifyingParams.QPD19?.value?.toString() || ''}
      name="QPD19"
      onChange={onNumericInputChange}
      suffix={t('emissionQualifyingParams:fields.QPD19.suffix')}
    />
  )

  const qpd20ValidationResult = useMemo(() => {
    return qpd20Schema.safeParse(qualifyingParams.QPD20)
  }, [qualifyingParams.QPD20])

  const hasQpd20RangeError = useMemo(() => {
    return (
      qpd20ValidationResult.error?.issues.some(
        issue =>
          (issue.code === 'too_small' || issue.code === 'too_big') && issue.path.includes('value')
      ) || false
    )
  }, [qpd20ValidationResult])

  const qpd20Editor = (
    <QpdWithItemsEditor
      strings={{
        itemPlaceholderName: t('emissionQualifyingParams:fields.QPD20.itemNamePlaceholder'),
        label: t('emissionQualifyingParams:fields.QPD20.label'),
        hint: t('emissionQualifyingParams:fields.QPD20.hint'),
        rangeError: t('emissionQualifyingParams:fields.QPD20.errors.rangeError'),
        instructions: t('emissionQualifyingParams:fields.QPD20.instructions')
      }}
      items={qualifyingParams.QPD20?.materials || []}
      hasRangeError={hasQpd20RangeError}
      onNameChange={onNameChange}
      onValueChange={onValueChange}
      onRemoveOtherElement={onRemoveOtherElement}
      onAddOtherElement={onAddOtherElement}
      maxItems={MAX_OTHER_ELEMENTS}
      itemUnitSuffix={UnitLabel.percent}
    />
  )

  return (
    <div className="space-y-4">
      <ul className="grid grid-cols-2 gap-4">
        {isCodeRelevant('QPD10') && <div className="space-y-2">{qpd10}</div>}
        {isCodeRelevant('QPD11') && <div className="space-y-2">{qpd11}</div>}
        {isCodeRelevant('QPD12') && <div className="space-y-2">{qpd12}</div>}
        {isCodeRelevant('QPD13') && <div className="space-y-2">{qpd13}</div>}
        {isCodeRelevant('QPD14') && <div className="space-y-2">{qpd14}</div>}
        {isCodeRelevant('QPD15') && <div className="space-y-2">{qpd15}</div>}
        {isCodeRelevant('QPD16') && <div className="space-y-2">{qpd16}</div>}
        {isCodeRelevant('QPD17') && <div className="space-y-2">{qpd17}</div>}
        {isCodeRelevant('QPD18') && <div className="space-y-2">{qpd18}</div>}
        {isCodeRelevant('QPD19') && <div className="space-y-2">{qpd19}</div>}
        {isCodeRelevant('QPD20') && <div className="space-y-2">{qpd20Editor}</div>}
      </ul>
    </div>
  )
}
